import { z } from 'zod';

/**
 * Notifications (M33): the bell on mogzo.com, and emails for a player's turn while they're
 * away. Games, communities and achievements make them; each player chooses which they get.
 */

export const NOTIFICATION_KINDS = [
  'turn',
  'turn_reminder',
  'match_started',
  'match_ended',
  'rematch',
  'base_raided',
  'reply',
  'mention',
  'achievement',
] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

/** Which setting turns a kind on or off, and which filter on the bell shows it. */
export const NOTIFICATION_GROUPS: Record<NotificationKind, 'games' | 'replies' | 'achievements'> = {
  turn: 'games',
  turn_reminder: 'games',
  match_started: 'games',
  match_ended: 'games',
  rematch: 'games',
  base_raided: 'games',
  reply: 'replies',
  mention: 'replies',
  achievement: 'achievements',
};

/** Kinds that can also come by email, for players who turned it on. */
export const EMAIL_KINDS: readonly NotificationKind[] = ['turn', 'turn_reminder', 'rematch'];

export const NOTIFICATION_LIMITS = {
  /** How long the bell keeps them. */
  keepDays: 30,
  /** An email waits this long, unread, before it goes (the player may be looking already). */
  emailAfterMinutes: 2,
  /** A player is away when MOGZO hasn't heard from them for this long. */
  awayMinutes: 5,
  /** At most one email per game in this long. */
  emailEveryMinutes: 60,
  /** A turn of an hour or more gets a reminder when this share of it is left (or 15 minutes). */
  reminderShare: 0.25,
  /** The bell's list, a page at a time. */
  page: 30,
  /** How often the web asks for the unread count, in milliseconds. */
  pollMs: 60_000,
} as const;

/** One notification as the bell shows it. */
export interface NotificationView {
  id: string;
  kind: NotificationKind;
  title: string;
  detail: string;
  /** Where it opens on mogzo.com. */
  path: string;
  /** What its picture is: the game, or the person who did it. */
  icon: { kind: 'game'; name: string } | { kind: 'person'; name: string } | { kind: 'achievement' };
  read: boolean;
  createdAt: string;
}

export interface NotificationSettings {
  games: boolean;
  replies: boolean;
  achievements: boolean;
  /** Emails for your turn and rematches while you're away. Off until the player turns it on. */
  emailGames: boolean;
  /** Games the player stopped emails for. */
  muted: { projectId: string; name: string }[];
}

export const notificationSettingsSchema = z
  .object({
    games: z.boolean(),
    replies: z.boolean(),
    achievements: z.boolean(),
    emailGames: z.boolean(),
  })
  .partial()
  .strict();

/** `@username` in a post or comment: the usernames it mentions, at most 10. */
export function mentionsIn(text: string): string[] {
  const found = new Set<string>();
  for (const match of text.matchAll(/(?:^|[^\w@])@([a-z0-9_]{3,20})\b/gi)) {
    const name = match[1]?.toLowerCase();
    if (name) found.add(name);
    if (found.size >= 10) break;
  }
  return [...found];
}
